//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025-2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useEffect, useRef } from 'react';

// Increased from 100ms to 2000ms to reduce CPU usage from high-frequency DOM
// queries. The trade-off is that index-pattern errors persist for up to 2s
// before detection, but testing showed this error is rare enough to be acceptable.
const INDEX_PATTERN_POLLING_INTERVAL = 2000;
const PAUSE_FOR_RELOAD = 2000; // Wait to resume poling so that the browser can change the location

//Occasionally on loading a dashboard, there is a problem and the index page is loaded,
//so we will re-load the dashboard
export function useDashboardIframePolling(
  iframeRef: React.RefObject<HTMLIFrameElement>,
  dashboardUrl: string,
  setDashboardLoaded: (v: boolean) => void,
  dashboardLoaded: boolean,
  triggerPolling: boolean = true,
) {
  const pauseForReload = useRef(false);

  useEffect(() => {
    if (!triggerPolling) return;
    if (!iframeRef.current) return;

    pauseForReload.current = false;

    const pollIndexPattern = setInterval(() => {
      const doc = iframeRef.current?.contentDocument;

      if (pauseForReload.current) return;

      if (doc?.querySelector('.dshAppContainer')) {
        setDashboardLoaded(true);
        clearInterval(pollIndexPattern);
      }

      if (doc?.querySelector('[data-test-subj="indexPatternTable"]')) {
        if (iframeRef.current && iframeRef.current.contentWindow) {
          iframeRef.current.contentWindow.location = dashboardUrl;
          pauseForReload.current = true;
          setTimeout(() => {
            pauseForReload.current = false;
          }, PAUSE_FOR_RELOAD);
        }
      }
    }, INDEX_PATTERN_POLLING_INTERVAL);

    return () => {
      if (pollIndexPattern) clearInterval(pollIndexPattern);
    };
  }, [
    iframeRef,
    dashboardUrl,
    setDashboardLoaded,
    dashboardLoaded,
    triggerPolling,
  ]);
}
